import React, { createContext, useContext, useEffect, useState, PropsWithChildren } from "react";
import { ACCESS_DENIED } from "../lib/auth";
import { useGrpcClient, usePropertyOptionalGrpcCall } from "../lib/grpc";
import { PropertyAPIPromiseClient } from "../gen/witbooking/backoffice/property/v1/property_api_grpc_web_pb";
import { GetPropertyConfigurationRequest } from "../gen/witbooking/backoffice/property/v1/property_api_pb";
import { EstablishmentAPIPromiseClient } from "../gen/witbooking/backoffice/property/v1/establishment_api_grpc_web_pb";
import { GetPropertyRequest } from "../gen/witbooking/backoffice/property/v1/establishment_api_pb";

export type PropertyConfigurationValue = { [key: string]: string } | undefined | null;

const PropertyConfiguration = createContext<PropertyConfigurationValue>(undefined);

const CONFIG_KEYS = [
  "showServicesManager",
  "numberOfPromotionsGroup",
  "showEmailTemplateManager",
  "showAdvancedEmailsManager",
  "showNewReservationsList",
  "showBatchCancellation",
  "showPackagesManager",
  "showAccommodationsManager",
  "showBatchInventories",
  "showNewAriManagement",
  "showInventoryTable",
  "showHistoricalReservations",
  "useAriService",
  "taxesText",
  "gtmCodes",
  "propertyImageLimit",
  "showAltARIManagerStyles",
  "numberOfDaysARIManager"
];
export function PropertyConfigurationProvider({ children }: PropsWithChildren<{}>) {
  const [propertyConfig, setPropertyConfig] = useState<{ [key: string]: string } | undefined>();
  const propertyGrpcClient = useGrpcClient(PropertyAPIPromiseClient);
  const establishmentGrpcClient = useGrpcClient(EstablishmentAPIPromiseClient);

  const getPropertyConfiguration = usePropertyOptionalGrpcCall(
    propertyGrpcClient,
    propertyGrpcClient.getPropertyConfiguration
  );
  const getEstablishmentConfiguration = usePropertyOptionalGrpcCall(
    establishmentGrpcClient,
    establishmentGrpcClient.getProperty
  );

  useEffect(() => {
    async function load() {
      try {
        if (!getPropertyConfiguration || !getEstablishmentConfiguration) {
          return;
        }
        const requestPropertyConfig = new GetPropertyConfigurationRequest();
        const requestEstablishmentConfig = new GetPropertyRequest();

        CONFIG_KEYS.forEach(key => requestPropertyConfig.addConfigurationKeys(key));

        const [responsePropertyConfiguration, responseEstablishmentConfig] = await Promise.all([
          getPropertyConfiguration(requestPropertyConfig),
          getEstablishmentConfiguration(requestEstablishmentConfig),
        ]);

        const propertyConfiguration = responsePropertyConfiguration
          .toObject()
          .configurationsMap.reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {} as { [key: string]: string });

        const timeZone = responseEstablishmentConfig.getProperty()?.toObject()?.location?.timeZone;

        setPropertyConfig({
          ...propertyConfiguration,
          ...(timeZone && { timeZone }),
        });
      } catch (error) {
        if (error === ACCESS_DENIED) {
          console.error("Access denied", error);
        } else {
          console.error("Error retrieving rpt", error);
        }
        setPropertyConfig({});
      }
    }

    load();
  }, [getPropertyConfiguration, getEstablishmentConfiguration]);

  return (
    <PropertyConfiguration.Provider value={propertyConfig}>
      {children}
    </PropertyConfiguration.Provider>
  );
}

export function usePropertyConfiguration(): PropertyConfigurationValue {
  return useContext(PropertyConfiguration);
}

//This one is for compatibility with witadmin
export function withPropertyConfiguration<T>(Component: React.ComponentType<T>) {
  return function WrappedPropertyConfigComponent(props: T) {
    return (
      <PropertyConfiguration.Consumer>
        {(value: PropertyConfigurationValue) => <Component {...props} propertyConfig={value} />}
      </PropertyConfiguration.Consumer>
    );
  };
}
